import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { get, noop, uniqBy } from 'lodash';

import * as api from 'lib/apiV6';
import { handleError } from 'slices/app';
import errorMessages from 'components/FormattedError/messages';
import { FetchingStatusesActionTypes } from './fetchingStatuses';
import { createStatusActions } from './utils';

const SLICE_NAME = 'bannedLinks';

export type BansStatus = 'active' | 'removed';

export type BanTarget = { url: string } | { profileId: number };

// a refused request explains itself in one of these fields; a gateway error, a broken session or a dropped
// connection do not, and they still have to be visible
const ERROR_FIELDS = ['url', 'profile_id', 'file'];

export const handleBanError = async (e: any): Promise<void> => {
  const payload = await e?.response?.json?.().catch(noop);
  const field = ERROR_FIELDS.find((name) => payload?.[name]);

  if (field) {
    handleError({ content: payload[field] });
  } else {
    handleError({
      content: [get(errorMessages, e?.response?.status, errorMessages.defaultMessage)],
      formatted: false,
    });
  }
};

export const receiveBans = createAsyncThunk(
  `${SLICE_NAME}/receiveBans`,
  async (params: { offset: number; q: string; status: BansStatus }, { rejectWithValue }) => {
    try {
      const result = await api.bansGet(params);

      return { ...result.payload, offset: params.offset };
    } catch (e) {
      await handleBanError(e);

      return rejectWithValue(e);
    }
  }
);

export const createBan = createAsyncThunk(
  `${SLICE_NAME}/createBan`,
  async (
    {
      target,
      details,
      onSuccess = noop,
    }: { details: API$BanDetails; target: BanTarget; onSuccess?: () => void },
    { rejectWithValue, dispatch }
  ) => {
    const { setError, setLoading, setSuccess } = createStatusActions(
      dispatch,
      FetchingStatusesActionTypes.bannedLinkAdded
    );

    try {
      setLoading();

      const result = await ('url' in target
        ? api.banCreate({ ...details, url: target.url })
        : api.banProfile({ ...details, profile_id: target.profileId }));

      setSuccess();
      onSuccess();
      return result.payload;
    } catch (e) {
      setError();
      await handleBanError(e);

      return rejectWithValue(e);
    }
  }
);

export const removeBan = createAsyncThunk(
  `${SLICE_NAME}/removeBan`,
  async (
    {
      ban,
      reason,
      onSuccess = noop,
    }: { ban: API$ProfileBan; reason: string; onSuccess?: () => void },
    { rejectWithValue, dispatch }
  ) => {
    const { setError, setLoading, setSuccess } = createStatusActions(
      dispatch,
      FetchingStatusesActionTypes.bannedLinkDeleted
    );

    try {
      setLoading();

      await api.banRemove(ban.id, reason);
      setSuccess();
      onSuccess();

      return ban;
    } catch (e) {
      setError();
      await handleBanError(e);

      return rejectWithValue(e);
    }
  }
);

export type BannedLinksState = {
  bans: Array<API$ProfileBan>;
  count: number;
  isListFailed: boolean;
  // the list request on its way: only the answer to the latest one is shown, tabs and search can change meanwhile
  listRequestId: string | null;
};

export const bannedLinksInitialState: BannedLinksState = {
  bans: [],
  count: 0,
  listRequestId: null,
  isListFailed: false,
};

const bannedLinksSlice = createSlice({
  name: SLICE_NAME,
  initialState: bannedLinksInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(receiveBans.pending, (state, action) => {
        state.listRequestId = action.meta.requestId;
        state.isListFailed = false;

        // rows of the previous tab or search must not stay under the new filter, even if this request fails
        if (!action.meta.arg.offset) {
          state.bans = [];
          state.count = 0;
        }
      })
      .addCase(receiveBans.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) {
          return;
        }

        const { count, offset, results } = action.payload;

        // a ban added since the first page shifts the offset and repeats the last row
        state.bans = offset ? uniqBy([...state.bans, ...results], 'id') : results;
        state.count = count;
        state.listRequestId = null;
      })
      .addCase(receiveBans.rejected, (state, action) => {
        if (action.meta.requestId === state.listRequestId) {
          state.listRequestId = null;
          state.isListFailed = true;
        }
      });
  },
});

export const bannedLinks = bannedLinksSlice.reducer;
